import { useState } from 'react';
import { colors, Card, Button, LoadingSpinner } from '../../shared';
import { Play, ArrowLeft, Copy, Check, AlertCircle } from 'lucide-react';

const OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY;

export default function PromptTestSandbox({ prompt, onBack }) {
  const [variables, setVariables] = useState(() => {
    // Initialize variables with empty values
    const initial = {};
    (prompt.templateVariables || []).forEach(v => {
      initial[v.name] = v.type === 'object' ? '{}' : '';
    });
    return initial;
  });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [latency, setLatency] = useState(null);
  const [copied, setCopied] = useState(false);

  // Build the final prompt with variables substituted
  const buildFinalPrompt = () => {
    let userPrompt = prompt.userPromptTemplate;
    Object.entries(variables).forEach(([key, value]) => {
      userPrompt = userPrompt.replace(new RegExp(`{{${key}}}`, 'g'), value);
    });
    return userPrompt;
  };

  const handleTest = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    const startTime = Date.now();

    try {
      const userContent = buildFinalPrompt();

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${OPENAI_API_KEY}`,
        },
        body: JSON.stringify({
          model: prompt.model,
          messages: [
            { role: 'system', content: prompt.systemPrompt },
            { role: 'user', content: userContent },
          ],
          max_tokens: prompt.maxTokens,
          temperature: prompt.temperature,
        }),
      });

      const endTime = Date.now();
      setLatency(endTime - startTime);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `API error: ${response.status}`);
      }

      const data = await response.json();
      const content = data.choices[0]?.message?.content || '';

      // Parse JSON if expected
      if (prompt.outputFormat === 'json') {
        try {
          const cleaned = content.replace(/```json\n?|\n?```/g, '').trim();
          setResult({
            raw: content,
            parsed: JSON.parse(cleaned),
            tokens: {
              input: data.usage?.prompt_tokens || 0,
              output: data.usage?.completion_tokens || 0,
            },
          });
        } catch (parseError) {
          setResult({
            raw: content,
            parseError: parseError.message,
            tokens: {
              input: data.usage?.prompt_tokens || 0,
              output: data.usage?.completion_tokens || 0,
            },
          });
        }
      } else {
        setResult({
          raw: content,
          tokens: {
            input: data.usage?.prompt_tokens || 0,
            output: data.usage?.completion_tokens || 0,
          },
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(result?.raw || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onBack}
          className="p-2 rounded-lg"
          style={{ background: colors.cardBg, color: colors.textMuted }}
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: colors.textPrimary }}>
            Test Sandbox
          </h1>
          <p className="text-sm" style={{ color: colors.textSecondary }}>
            {prompt.name} ({prompt.id})
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Left: Variables Input */}
        <div>
          <Card className="mb-4">
            <h2 className="text-lg font-semibold mb-4" style={{ color: colors.textPrimary }}>
              Test Variables
            </h2>

            {(prompt.templateVariables || []).length === 0 ? (
              <p className="text-sm" style={{ color: colors.textMuted }}>
                No template variables defined.
              </p>
            ) : (
              <div className="space-y-4">
                {(prompt.templateVariables || []).map((variable) => (
                  <div key={variable.name}>
                    <label className="block text-sm font-medium mb-1" style={{ color: colors.textSecondary }}>
                      {variable.name}
                      <span className="ml-2 text-xs" style={{ color: colors.textMuted }}>
                        ({variable.type})
                      </span>
                    </label>
                    {variable.description && (
                      <p className="text-xs mb-2" style={{ color: colors.textMuted }}>
                        {variable.description}
                      </p>
                    )}
                    {variable.type === 'object' || variable.type === 'array' ? (
                      <textarea
                        value={variables[variable.name] || ''}
                        onChange={(e) => setVariables({ ...variables, [variable.name]: e.target.value })}
                        rows={4}
                        className="w-full px-3 py-2 rounded-lg font-mono text-sm outline-none"
                        style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
                        placeholder={variable.type === 'object' ? '{"key": "value"}' : '["item1", "item2"]'}
                      />
                    ) : (
                      <input
                        type={variable.type === 'number' ? 'number' : 'text'}
                        value={variables[variable.name] || ''}
                        onChange={(e) => setVariables({ ...variables, [variable.name]: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                        style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
                        placeholder={`Enter ${variable.name}...`}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            <Button
              onClick={handleTest}
              disabled={loading}
              className="w-full mt-4"
            >
              {loading ? <LoadingSpinner size="sm" color={colors.black} /> : <Play size={18} />}
              {loading ? 'Running...' : 'Run Test'}
            </Button>
          </Card>

          {/* Prompt Preview */}
          <Card>
            <h2 className="text-lg font-semibold mb-2" style={{ color: colors.textPrimary }}>
              Final Prompt Preview
            </h2>
            <div className="mb-3">
              <p className="text-xs font-medium mb-1" style={{ color: colors.textMuted }}>System:</p>
              <pre
                className="text-xs p-2 rounded overflow-auto max-h-24"
                style={{ background: colors.inputBg, color: colors.textSecondary }}
              >
                {prompt.systemPrompt}
              </pre>
            </div>
            <div>
              <p className="text-xs font-medium mb-1" style={{ color: colors.textMuted }}>User:</p>
              <pre
                className="text-xs p-2 rounded overflow-auto max-h-48"
                style={{ background: colors.inputBg, color: colors.textSecondary }}
              >
                {buildFinalPrompt()}
              </pre>
            </div>
          </Card>
        </div>

        {/* Right: Result */}
        <div>
          <Card className="h-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold" style={{ color: colors.textPrimary }}>
                Result
              </h2>
              {result && (
                <div className="flex items-center gap-3 text-xs" style={{ color: colors.textMuted }}>
                  <span>{latency}ms</span>
                  <span>{result.tokens?.input} in / {result.tokens?.output} out tokens</span>
                  <button
                    onClick={handleCopy}
                    className="p-1.5 rounded"
                    style={{ background: colors.cardBg }}
                  >
                    {copied ? <Check size={14} style={{ color: colors.green }} /> : <Copy size={14} />}
                  </button>
                </div>
              )}
            </div>

            {error && (
              <div className="p-3 rounded-lg mb-4 flex items-start gap-2" style={{ background: colors.redDim }}>
                <AlertCircle size={18} style={{ color: colors.red }} />
                <p className="text-sm" style={{ color: colors.red }}>{error}</p>
              </div>
            )}

            {result ? (
              <div>
                {result.parseError && (
                  <div className="p-2 rounded mb-3 text-xs" style={{ background: colors.orangeDim, color: colors.orange }}>
                    JSON Parse Error: {result.parseError}
                  </div>
                )}

                {result.parsed && (
                  <div className="mb-4">
                    <p className="text-xs font-medium mb-1" style={{ color: colors.textMuted }}>Parsed JSON:</p>
                    <pre
                      className="text-xs p-3 rounded overflow-auto max-h-48"
                      style={{ background: colors.inputBg, color: colors.green }}
                    >
                      {JSON.stringify(result.parsed, null, 2)}
                    </pre>
                  </div>
                )}

                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: colors.textMuted }}>Raw Output:</p>
                  <pre
                    className="text-sm p-3 rounded overflow-auto whitespace-pre-wrap"
                    style={{ background: colors.inputBg, color: colors.textPrimary, maxHeight: '400px' }}
                  >
                    {result.raw}
                  </pre>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64" style={{ color: colors.textMuted }}>
                <p className="text-sm">Run a test to see results</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
